import type { FC} from 'react';
import { useCallback, useState } from 'react';
import { ErrorOutline } from '@mui/icons-material';
import { Box, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, Unstable_Grid2 as Grid, InputLabel, MenuItem, Select, Stack, SvgIcon, Switch, TextField, Typography, Chip, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import axios from 'axios';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { has } from 'lodash';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { Announcement_Type } from '@/types/announcement';
import { config } from '@/config';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import ProgressButton from '@/components/common/buttons/progress-button';
import ProfessionalDialog, { DialogActionButtons } from '@/components/common/professional-dialog';
import { Megaphone as MegaphoneIcon } from '@phosphor-icons/react';
import FileUploader, { type FileUpload, type UploadedFile } from '@/components/common/FileUploader';

interface CreateAnnouncementDialogProps {
  open: boolean;
  onClose: () => void;
}

const announcementTypes = [Announcement_Type.GENERAL, Announcement_Type.MAINTENANCE, Announcement_Type.EMERGENCY];

const CreateAnnouncementDialog: FC<CreateAnnouncementDialogProps> = ({ open, onClose }) => {
  const { user, community } = useUser();
  const navigate = useNavigate();
  const [scheduled, setScheduled] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileUpload[]>([]);
  
  const formik = useFormik({
    initialValues: {
      subject: '',
      message: '',
      type: Announcement_Type.GENERAL,
      board_members_only: false,
      send_email: false,
      send_push: false,
      send_sms: false,
      is_draft: false,
      community_id: user?.selected_community_id,
      scheduled_at: null,
      files: [] as UploadedFile[],
    },
    validationSchema: Yup.object({
      subject: Yup.string().max(200, 'Subject must be less than 200 characters'),
      message: Yup.string().required('Message is required').min(10, 'Message must be at least 10 characters'),
      type: Yup.string().required('Type is required'),
      board_members_only: Yup.boolean(),
      send_email: Yup.boolean(),
      send_push: Yup.boolean(),
      send_sms: Yup.boolean(),
      is_draft: Yup.boolean(),
      community_id: Yup.string().required('Community is required'),
      scheduled_at: Yup.mixed().nullable().test(
        'not-in-past',
        'Scheduled time must be in the future',
        (value) => {
          if (!value) return true;
          return dayjs(value as any).isAfter(dayjs());
        }
      ),
      files: Yup.array(),
    }),
    onSubmit: async (values) => {
      console.log(values);
      try {
        axios.defaults.withCredentials = true;
        await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
        
        // Prepare the payload with file attachments
        const payload = {
          ...values,
          files: uploadedFiles.map(file => file.uploadedFile).filter(Boolean),
        };
        
        const response = await axios.post(`${config.site.api}/api/v1/announcements`, payload, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        const data = response.data;
        if (has(data, 'error')) {
          toast.error(data.error);
          return;
        }
        toast.success('Announcement created successfully');
        handleClose();
        navigate(paths.announcements.announcement(data.data.uuid ?? String(data.data.id)));
      } catch (error: any) {
        console.error(error?.message);
        toast.error('Unable to complete request');
      }
    },
  });

  console.log(formik.errors);

  const handleClose = useCallback((): void => {
    onClose();
    formik.resetForm();
    setScheduled(false);
    setUploadedFiles([]);
  }, [onClose, formik]);

  const handleFileUploadComplete = useCallback((files: FileUpload[]) => {
    setUploadedFiles(files);
    formik.setFieldValue('files', files.map(file => file.uploadedFile).filter(Boolean));
  }, [formik]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case Announcement_Type.EMERGENCY:
        return 'error';
      case Announcement_Type.MAINTENANCE:
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <ProfessionalDialog
      actions={
        <DialogActionButtons
          confirmText={scheduled ? 'Schedule For Later' : 'Send Now'}
          loading={formik.isSubmitting}
          onCancel={handleClose}
          onConfirm={formik.handleSubmit as any}
        />
      }
      contentSx={{ pt: 2, px: 3, pb: 2 }}
      icon={<MegaphoneIcon size={22} />}
      maxWidth="md"
      onClose={handleClose}
      open={open}
      subtitle="Compose and distribute an update to your community"
      title="Create Announcement"
      variant="default"
    >
      <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            {/* Subject and Type Row */}
            <Box display="flex" gap={2} justifyContent="space-between">
              <TextField
                error={formik.touched.subject ? Boolean(formik.errors.subject) : null}
                fullWidth
                helperText={formik.touched.subject ? formik.errors.subject : null}
                label="Subject"
                name="subject"
                onChange={formik.handleChange}
                placeholder="Enter a clear, concise subject for your announcement..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                value={formik.values.subject}
                variant="outlined"
              />
              <FormControl fullWidth>
                <InputLabel id="announcement-type-label">Type</InputLabel>
                <Select
                  error={formik.touched.type ? Boolean(formik.errors.type) : null}
                  label="Type"
                  labelId="announcement-type-label"
                  name="type"
                  onChange={formik.handleChange}
                  sx={{
                    borderRadius: 2,
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }
                  }}
                  value={formik.values.type}
                  variant="outlined"
                >
                  {announcementTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Chip 
                        color={getTypeColor(type) as any} 
                        label={type} 
                        size="small"
                        variant="outlined"
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Message */}
            <TextField
              error={formik.touched.message ? Boolean(formik.errors.message) : null}
              fullWidth
              helperText={
                (formik.touched.message && formik.errors.message) || 
                `${formik.values.message.length}/2000 characters`
              }
              label="Message"
              multiline
              name="message"
              onChange={formik.handleChange}
              placeholder="Write your announcement message here. Be clear and informative..."
              rows={8}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              value={formik.values.message}
              variant="outlined"
            />

            {/* File Upload */}
            <Box>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 2 }} variant="subtitle2">
                Attachments (Optional)
              </Typography>
              <FileUploader
                allowedTypes={['IMAGES', 'DOCUMENTS', 'SPREADSHEETS', 'PRESENTATIONS']}
                maxFileSize={25 * 1024 * 1024} // 25MB
                maxFiles={5}
                onUploadComplete={handleFileUploadComplete}
                showPreviews
                subtitle="Upload documents, images, or other files to include with your announcement"
                title="Add Files to Announcement"
              />
            </Box>

            {/* Settings Section */}
            <Box>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 2 }} variant="subtitle2">
                Announcement Settings
              </Typography>
              
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.board_members_only}
                      name="board_members_only"
                      onChange={formik.handleChange}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 500 }} variant="body2">
                        Board Members Only
                      </Typography>
                      <Typography color="text.secondary" variant="caption">
                        Restrict this announcement to board members only
                      </Typography>
                    </Box>
                  }
                />
                
                <Divider sx={{ my: 2 }} />
                
                <Typography sx={{ fontWeight: 500, mb: 1 }} variant="body2">
                  Notification Methods
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2, display: 'block' }} variant="caption">
                  Members will always receive the notification inside the application.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={formik.values.send_email} 
                      name="send_email" 
                      onChange={formik.handleChange} 
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 500 }} variant="body2">
                        Send Email
                      </Typography>
                      <Typography color="text.secondary" variant="caption">
                        Send announcement as email to all recipients
                      </Typography>
                    </Box>
                  }
                />
                
                <Divider sx={{ my: 2 }} />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={scheduled}
                      name="scheduled"
                      onChange={() => {
                        setScheduled(!scheduled);
                        if (scheduled) {
                          formik.setFieldValue('scheduled_at', null);
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 500 }} variant="body2">
                        Schedule for Later
                      </Typography>
                      <Typography color="text.secondary" variant="caption">
                        Schedule this announcement to be sent at a specific time
                      </Typography>
                    </Box>
                  }
                />
              </FormGroup>
            </Box>

            {/* Scheduled Date Picker */}
            {scheduled ? (
              <Box>
                <Box display="flex" gap={2}>
                  <DatePicker
                    disablePast
                    label="Scheduled Date"
                    minDate={dayjs()}
                    onChange={(date) => {
                      const current = formik.values.scheduled_at ? dayjs(formik.values.scheduled_at as any) : null;
                      if (date) {
                        const merged = (current ?? dayjs().hour(9).minute(0))
                          .year(date.year())
                          .month(date.month())
                          .date(date.date());
                        formik.setFieldValue('scheduled_at', merged);
                      } else {
                        formik.setFieldValue('scheduled_at', null);
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                    value={formik.values.scheduled_at ? dayjs(formik.values.scheduled_at as any) : null}
                  />
                  <TimePicker
                    ampm
                    label="Scheduled Time"
                    onChange={(time) => {
                      const current = formik.values.scheduled_at ? dayjs(formik.values.scheduled_at as any) : null;
                      if (time) {
                        const merged = (current ?? dayjs().add(1, 'day'))
                          .hour(time.hour())
                          .minute(time.minute())
                          .second(0);
                        formik.setFieldValue('scheduled_at', merged);
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: 'Select a time (e.g. 2:00 PM)',
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                    value={formik.values.scheduled_at ? dayjs(formik.values.scheduled_at as any) : null}
                  />
                </Box>

                {formik.errors.scheduled_at && formik.touched.scheduled_at ? (
                  <Typography color="error" sx={{ mt: 1 }} variant="caption">
                    {formik.errors.scheduled_at as string}
                  </Typography>
                ) : null}

                <Typography color="text.secondary" sx={{ mt: 1.5, display: 'block' }} variant="caption">
                  Community timezone: {community?.timezone ?? 'Not set'}
                </Typography>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    You can cancel a scheduled announcement any time before it sends.
                  </Typography>
                </Alert>
              </Box>
            ) : null}

          </Stack>
        </form>
    </ProfessionalDialog>
  );
};

export default CreateAnnouncementDialog;
